import { ChevronRight, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import Dropdown from '../Dropdown.jsx';
import { isGroup } from './chatInput.utils.js';
import { resolveIcon } from './iconMap.js';

const ChatInputAddMenu = ({ items, disabled, onSelect }) => {
  const [key, setKey] = useState(0);
  const [path, setPath] = useState([]);

  const current = useMemo(() => {
    let entries = items;
    for (const step of path) {
      entries = step.items;
    }
    return entries;
  }, [items, path]);

  const closeMenu = () => {
    setPath([]);
    setKey((prev) => prev + 1);
  };

  const handleLeaf = (entry) => {
    const value = entry.value ?? entry.text;
    onSelect?.(value, { label: entry.text ?? value, ...entry });
    closeMenu();
  };

  const trigger = (
    <button
      type="button"
      disabled={disabled}
      className="inline-flex h-8 w-8 items-center justify-center rounded-full p-1 text-content-secondary hover:bg-surface-hover disabled:opacity-50"
      aria-label="Open add menu"
    >
      <Plus size={16} />
    </button>
  );

  return (
    <Dropdown
      key={key}
      trigger={trigger}
      menuPosition="top"
      alignMenu="left"
      width="240px"
      menuMaxHeight="none"
      menuClassName="p-0"
    >
      <div className="scrollbar-aimsos-auto-sm max-h-[360px] overflow-y-auto py-1">
        {path.length > 0 ? (
          <button
            type="button"
            onClick={() => setPath((prev) => prev.slice(0, -1))}
            className="mb-1 flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-content hover:bg-surface-hover-subtle"
          >
            <ChevronRight size={14} className="rotate-180" />
            Back
          </button>
        ) : null}

        {(current ?? []).map((entry, index) => {
          if (!entry || typeof entry !== 'object') return null;
          if (isGroup(entry)) {
            return (
              <div key={`${entry.title ?? 'group'}-${index}`} className="pt-1">
                {entry.title ? (
                  <div className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-content-muted">
                    {entry.title}
                  </div>
                ) : null}
                {entry.items?.map((child, childIndex) => {
                  const childIcon = resolveIcon(child.icon);
                  const hasSubmenu = Array.isArray(child.items) && child.items.length > 0;
                  return (
                    <button
                      key={`${child.value ?? child.text ?? 'item'}-${childIndex}`}
                      type="button"
                      onClick={() => {
                        if (hasSubmenu) {
                          setPath((prev) => [...prev, child]);
                          return;
                        }
                        handleLeaf(child);
                      }}
                      className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm text-content-secondary hover:bg-surface-hover-subtle"
                    >
                      <span className="inline-flex items-center gap-2">
                        {childIcon ? <childIcon size={15} /> : null}
                        {child.text}
                      </span>
                      {hasSubmenu ? <ChevronRight size={14} /> : null}
                    </button>
                  );
                })}
              </div>
            );
          }

          const Icon = resolveIcon(entry.icon);
          const hasSubmenu = Array.isArray(entry.items) && entry.items.length > 0;
          return (
            <button
              key={`${entry.value ?? entry.text ?? 'item'}-${index}`}
              type="button"
              onClick={() => {
                if (hasSubmenu) {
                  setPath((prev) => [...prev, entry]);
                  return;
                }
                handleLeaf(entry);
              }}
              className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm text-content-secondary hover:bg-surface-hover-subtle"
            >
              <span className="inline-flex items-center gap-2">
                {Icon ? <Icon size={15} /> : null}
                {entry.text}
              </span>
              {hasSubmenu ? <ChevronRight size={14} /> : null}
            </button>
          );
        })}
      </div>
    </Dropdown>
  );
};

export default ChatInputAddMenu;
